'use client';

import { useEffect, useRef, useState } from 'react';
import { VrmManager } from '@/lib/vrm/VrmManager';
import { detectDevicePerformanceTier } from '@/lib/vrm/utils/performanceUtils';
import styles from './VrmViewerBackground.module.scss';

export interface VrmViewerBackgroundProps {
  modelPath?: string;
  animationPath?: string;
  enableControls?: boolean;
  className?: string;
}

export default function VrmViewerBackground({
  modelPath = '/models/character-01.vrm',
  animationPath = '/animations/VRMA_MotionPack/vrma/VRMA_02.vrma',
  enableControls = true,
  className = ''
}: VrmViewerBackgroundProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const vrmManagerRef = useRef<VrmManager | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!containerRef.current) {
      return;
    }

    let isMounted = true;
    let initializationStarted = false;

    const initializeVRM = async () => {
      if (initializationStarted) {
        return;
      }
      initializationStarted = true;
      
      try {
        setIsLoading(true);
        setError(null);

        // Detect device performance tier
        const performanceTier = detectDevicePerformanceTier();
        
        // Get VRM manager instance
        const vrmManager = VrmManager.getInstance();
        vrmManagerRef.current = vrmManager;

        // Initialize with container and config
        if (containerRef.current && isMounted) {
          await vrmManager.initialize(containerRef.current, {
          modelPath,
          animationPath,
          deviceTier: performanceTier.tier,
          enableControls,
          cameraPosition: { x: 0, y: 1.4, z: 3 },
          lookAtPosition: { x: 0, y: 1.0, z: 0 }
          });
        }

        if (isMounted) {
          setIsLoading(false);
        }
      } catch (err) {
        if (isMounted) {
          console.error('Failed to initialize VRM:', err);
          setError('3Dモデルの読み込みに失敗しました');
          setIsLoading(false);
        }
      }
    };

    // Initialize VRM viewer only once
    initializeVRM();

    // Cleanup
    return () => {
      isMounted = false;
      if (vrmManagerRef.current) {
        vrmManagerRef.current.dispose();
        vrmManagerRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array to run only once - config changes handled separately

  // Separate effect for config updates after initialization
  useEffect(() => {
    const updateVrmConfig = async () => {
      if (vrmManagerRef.current && vrmManagerRef.current.isInitialized) {
        try {
          await vrmManagerRef.current.updateConfig({
            modelPath,
            animationPath,
            enableControls
          });
        } catch (err) {
          console.error('Failed to update VRM config:', err);
        }
      }
    };
    
    updateVrmConfig();
  }, [modelPath, animationPath, enableControls]);

  return (
    <div 
      ref={containerRef} 
      className={`${styles.vrmContainer} ${className}`}
      aria-label="3D Character Model"
    >
      {isLoading && (
        <div className={styles.loadingOverlay}>
          <div className={styles.loadingSpinner}>
            <div className={styles.spinner}></div>
            <p>3Dモデルを読み込み中...</p>
          </div>
        </div>
      )}
      
      {error && (
        <div className={styles.errorOverlay}>
          <p>{error}</p>
        </div>
      )}
    </div>
  );
}